import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useLoginMutation } from '@/store/api';
import { Button, Field } from '@/components/ui';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password')
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [login, { isLoading, error }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    const result = await login(values);
    if ('error' in result) return;
    // A platform admin has no organization, so the operator dashboard would
    // only 403 at them. Send each role to the area it can actually use.
    const session = result.data;
    navigate(!session.organization && session.user.isPlatformAdmin ? '/admin' : '/', {
      replace: true
    });
  });

  const message =
    error && 'data' in error
      ? ((error.data as { error?: { message?: string } })?.error?.message ??
        'That did not work. Check your email and password.')
      : null;

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* The left panel states what the product does in the operator's words. */}
      <div className="hidden flex-col justify-between bg-ink px-10 py-12 lg:flex">
        <p className="text-base font-semibold text-white">CallWaiter</p>
        <div className="max-w-md">
          <h1 className="text-hero text-white">
            Every call answered, every order written down.
          </h1>
          <p className="mt-4 text-small leading-relaxed text-white/60">
            Your phone line answers with your hours, your menu, and your answers. When it
            hears something it was not told about, it hands the caller to your staff instead
            of guessing.
          </p>
        </div>
        <p className="text-micro text-white/40">Built for restaurants that miss calls at dinner.</p>
      </div>

      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <h2 className="text-display">Sign in</h2>
          <p className="mt-1 text-small text-slate">Manage your phone line and orders.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <Field
              label="Email"
              type="email"
              autoComplete="email"
              {...register('email')}
              error={errors.email?.message}
            />
            <Field
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={errors.password?.message}
            />

            {message ? (
              <p role="alert" className="rounded bg-rust-soft px-3 py-2 text-small text-rust">
                {message}
              </p>
            ) : null}

            <Button type="submit" loading={isLoading} className="w-full">
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-small text-slate">
            New here?{' '}
            <Link to="/register" className="text-jade underline underline-offset-2">
              Set up your restaurant
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
